/**
 * What the phone layout's AppBar shows (below md): a page publishes its
 * back arrow, title, second line, up to 2 icon actions and a trailing node
 * (the '...' menu) with useMobileHeader; the shell's MobileHeaderBar reads
 * them with useMobileHeaderState. Desktop never reads it.
 */
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

export interface MobileHeaderAction {
  key: string;
  /** Required: the button's aria-label (icon-only button). */
  label: string;
  icon: ReactNode;
  onClick: () => void;
  /** Drawn in the primary colour (e.g. search open). */
  active?: boolean;
  /** Shows a spinner and disables the button (e.g. refreshing). */
  busy?: boolean;
}

export interface MobileHeaderState {
  /** Replaces the hamburger with a back arrow. */
  back?: { label: string; onClick: () => void };
  /** One line (two with wrapTitle); the shell's page title when absent. */
  title?: ReactNode;
  /** The title may wrap onto a second line instead of being cut off; the
   *  bar grows to fit. */
  wrapTitle?: boolean;
  /** An optional smaller line under the title. */
  subtitle?: ReactNode;
  /** At most 2 (more are not drawn). */
  actions?: MobileHeaderAction[];
  /** After the actions, e.g. the '...' menu. */
  trailing?: ReactNode;
}

/** Sets (state) or clears (null) the header for one publishing hook. */
type Publish = (owner: object, state: MobileHeaderState | null) => void;

interface Published {
  owner: object;
  state: MobileHeaderState;
}

const PublishContext = createContext<Publish | null>(null);
const StateContext = createContext<MobileHeaderState | null>(null);

export const MobileHeaderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [published, setPublished] = useState<Published | null>(null);

  // Clearing only removes the publisher's own header, so a page that
  // unmounts after the next page has published leaves that page's header.
  const publish = useCallback<Publish>((owner, state) => {
    setPublished((prev) => {
      if (state) return { owner, state };
      return prev && prev.owner === owner ? null : prev;
    });
  }, []);

  return (
    <PublishContext.Provider value={publish}>
      <StateContext.Provider value={published ? published.state : null}>{children}</StateContext.Provider>
    </PublishContext.Provider>
  );
};

/** The published state with handlers that call the page's latest ones, so
 *  a click never runs a stale closure when `deps` left a callback out. */
const withLatestHandlers = (
  state: MobileHeaderState,
  latest: { current: MobileHeaderState | null },
): MobileHeaderState => ({
  ...state,
  back: state.back
    ? { ...state.back, onClick: () => (latest.current?.back ?? state.back)?.onClick() }
    : undefined,
  actions: state.actions?.map((action) => ({
    ...action,
    onClick: () => {
      const current = latest.current?.actions?.find((a) => a.key === action.key);
      (current ?? action).onClick();
    },
  })),
});

/**
 * Publish this page's mobile header. It is set after mount and again
 * whenever `deps` change (as useEffect deps: list what the header shows),
 * cleared when `state` is null, and cleared on unmount. Without a
 * MobileHeaderProvider it does nothing.
 */
export const useMobileHeader = (state: MobileHeaderState | null, deps: React.DependencyList): void => {
  const publish = useContext(PublishContext);
  const ownerRef = useRef<object>({});
  const latestRef = useRef<MobileHeaderState | null>(state);
  latestRef.current = state;

  useEffect(() => {
    if (!publish) return;
    const current = latestRef.current;
    publish(ownerRef.current, current ? withLatestHandlers(current, latestRef) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publish, ...deps]);

  useEffect(() => {
    if (!publish) return undefined;
    const owner = ownerRef.current;
    return () => publish(owner, null);
  }, [publish]);
};

/** The header the current page published, or null (the shell's defaults). */
export const useMobileHeaderState = (): MobileHeaderState | null => useContext(StateContext);
